// Settings screen (spec §7.1.3): profiles, thresholds, defaults, legend, reset.
// Built from Basecoat cards, items, switches, fields and input groups.
import { resolveConfig, type Profile } from '../engine';
import { btn, clear, h, type Child } from './dom';
import { confirmAction } from './confirm';
import { badge, profileTag } from './preview';
import { CLASS_LABEL, CLASS_ORDER, DRAFT_KEY, enabledProfiles, onSettings, profileSet, resetSettings, SAMPLE_DRAFT, saveDraft, state, updateSettings } from './state';

const THRESHOLDS = [
  ['titleLimitPx', 'Title limit', 'px', 'Google cuts titles wider than this, measured in the desktop title font.'],
  ['descCharCap', 'Description cap', 'chars', 'Google cuts descriptions longer than this many characters.'],
  ['tightTitlePx', 'Tight title', 'px', 'A title with less room left than this is Tight.'],
  ['tightDescPx', 'Tight description', 'px', 'A description with less room left than this is Tight.'],
  ['lowShownPct', 'Low shown', '%', 'Warn when fewer than this share of words are fully shown.'],
  ['overflowTolerancePct', 'Overflow tolerance', '%', 'Overflow up to this much counts as fitting (and Tight).'],
] as const;

/** A Basecoat card: a header with the title, an optional description and action, then the body. */
function card(id: string, title: string, description: string | null, body: Child, action?: HTMLElement): HTMLElement {
  if (action) action.classList.add('card-action');
  return h('section', { class: 'card', 'aria-labelledby': id },
    h('header', null, h('h2', { id }, title), description ? h('p', null, description) : null, action),
    h('section', null, body));
}

/** A button that stays focusable when it has nothing to do (aria-disabled, not disabled), so focus
 *  isn't lost when a click makes it inactive. */
function actionButton(attrs: Record<string, unknown>, label: string, inactive: boolean, onClick: () => void): HTMLButtonElement {
  const b = h('button', { ...attrs, 'aria-disabled': String(inactive) }, label);
  b.addEventListener('click', () => { if (!inactive) onClick(); });
  return b;
}

/** A Basecoat switch. The aria-label names it, and lets a re-render put focus back on it. */
function switchInput(checked: boolean, label: string, onChange: (v: boolean) => void, id?: string): HTMLInputElement {
  const input = h('input', { type: 'checkbox', role: 'switch', class: 'input', id, 'aria-label': label });
  input.checked = checked;
  input.addEventListener('change', () => onChange(input.checked));
  return input;
}

function dims(p: Profile) {
  const t = p.title, d = p.description;
  return `${p.viewportWidth}px · title ${t.width}px × ${t.maxLines} · description ${d.width}px × ${d.maxLines} · ${p.engine === 'webkit' ? 'WebKit' : 'Chromium'}`;
}

export function settingsView(): HTMLElement {
  const root = h('div', { class: 'settings' });

  function render() {
    const intro = h('div', { class: 'page-intro' },
      h('h1', null, 'Settings'),
      h('p', null, 'Choose which devices to check, set your brand, and adjust when text counts as cut. Changes apply to Check right away. A finished bulk check keeps its old results until you click Re-run.'));
    const enabled = new Set(enabledProfiles().map(p => p.id));
    const defaults = resolveConfig();
    const s = state.settings;

    const setProfile = (id: string, on: boolean) => {
      const next = new Set(enabled);
      if (on) next.add(id); else next.delete(id);
      if (!next.size) { render(); return; }
      updateSettings({ profiles: profileSet.list.filter(p => next.has(p.id)).map(p => p.id) });
    };

    const onDefaults = profileSet.list.every(p => enabled.has(p.id) === !!p.default);
    const resetDevices = actionButton({ ...btn('outline', 'sm'), id: 'reset-devices' }, 'Reset to defaults', onDefaults, () => updateSettings({ profiles: null }));
    const selectAll = (c: (typeof CLASS_ORDER)[number]) => actionButton(
      { ...btn('ghost', 'xs'), id: `all-${c}`, 'aria-label': `Select all ${CLASS_LABEL[c].toLowerCase()} devices` }, 'Select all',
      profileSet.list.every(p => p.deviceClass !== c || enabled.has(p.id)),
      () => updateSettings({ profiles: profileSet.list.filter(p => enabled.has(p.id) || p.deviceClass === c).map(p => p.id) }));

    // Each device is a label, so a click anywhere on the row flips its switch.
    const profiles = card('s-prof', 'Devices',
      `${enabled.size} of ${profileSet.list.length} on. Check shows a preview for each device that’s on, and Bulk checks every row on each of them. Turn on the phones your visitors use; fewer devices make big bulk checks faster. At least one stays on.`,
      CLASS_ORDER.map(c => h('div', { class: 'prof-group' },
        h('div', { class: 'prof-group-head' }, h('h3', null, CLASS_LABEL[c]), selectAll(c)),
        h('div', { class: 'item-group' }, profileSet.list.filter(p => p.deviceClass === c).map(p => h('label', { class: 'item prof', 'data-size': 'sm' },
          h('span', { class: 'prof-main' },
            h('span', { class: 'prof-name' }, p.label, p.default ? h('span', { class: 'tag' }, 'Default') : null, profileTag(p)),
            h('span', { class: 'prof-dims mono' }, dims(p)),
            p.notes ? h('span', { class: 'prof-note' }, p.notes) : null),
          switchInput(enabled.has(p.id), p.label, v => setProfile(p.id, v))))))),
      resetDevices);

    const th = card('s-th', 'Thresholds',
      'These decide when text counts as Cut or Tight, and when to warn. The defaults come from observed Google behavior, so leave them unless you want to test a theory, for example that Google’s title limit has changed. Reset settings, below, puts them back.',
      h('div', { class: 'grid-2' }, THRESHOLDS.map(([key, label, unit, hint]) => {
        const id = `th-${key}`;
        const input = h('input', { id, type: 'number', min: '0', step: key === 'overflowTolerancePct' ? '0.1' : '1', inputmode: 'decimal', 'aria-describedby': `${id}-unit ${id}-hint` });
        input.value = String(s.thresholds[key] ?? defaults[key]);
        input.addEventListener('change', () => {
          const v = Number(input.value);
          const t = { ...state.settings.thresholds };
          if (!Number.isFinite(v) || v < 0 || v === defaults[key]) delete t[key]; else t[key] = v;
          updateSettings({ thresholds: t });
          input.value = String(t[key] ?? defaults[key]);
        });
        return h('div', { class: 'field' },
          h('label', { for: id }, label),
          h('div', { class: 'input-group' }, input, h('span', { id: `${id}-unit`, 'data-align': 'inline-end' }, unit)),
          h('p', { id: `${id}-hint` }, `${hint} Default ${defaults[key]}.`));
      })));

    const brandInput = h('input', { id: 's-brand', type: 'text', placeholder: 'e.g. Example', autocomplete: 'off', 'aria-describedby': 's-brand-hint' });
    brandInput.value = s.brand;
    brandInput.addEventListener('change', () => updateSettings({ brand: brandInput.value.trim() }));
    const defs = card('s-def', 'Defaults', null,
      h('div', { class: 'fieldset' },
        h('div', { class: 'field' },
          h('label', { for: 's-brand' }, 'Default brand'), brandInput,
          h('p', { id: 's-brand-hint' }, 'Your brand name as it appears in titles, such as “Example”. You get a warning when it’s cut. Used on Check, and for Bulk rows without a brand column.')),
        h('div', { class: 'field', 'data-orientation': 'horizontal' },
          switchInput(s.showHidden, 'Show hidden lines', v => updateSettings({ showHidden: v }), 's-hidden'),
          h('section', null,
            h('label', { for: 's-hidden' }, 'Show hidden lines'),
            h('p', null, 'Draw lines past the device’s line limit, faded, below a dashed rule.')))));

    const legend = card('s-leg', 'What the labels mean', null,
      h('ul', { class: 'legend-list' },
        h('li', null, badge('fits'), h('span', null, 'Every word shows, with room to spare.')),
        h('li', null, badge('tight'), h('span', null, 'Every word shows, but a few more characters would be cut.')),
        h('li', null, badge('cut'), h('span', null, 'Some words don’t show on this device.')),
        h('li', null, h('span', { class: 'tag tag-assumed' }, 'Unverified'), h('span', null, 'Assumed from a nearby verified device. Results may differ from Google.')),
        h('li', null, h('span', { class: 'tag tag-experimental' }, 'Experimental'), h('span', null, 'Layout not yet confirmed on a real device.')),
        h('li', null, h('s', { class: 'hid-google' }, 'text'), h('span', null, 'Cut by Google before it reached the device. Shorten the text.')),
        h('li', null, h('s', { class: 'hid-limit' }, 'text'), h('span', null, 'Hidden by the device’s line limit. Use narrower words or fewer of them.'))));

    const resetAll = h('button', btn('outline'), 'Reset settings');
    resetAll.addEventListener('click', () => resetSettings());
    const resetDraft = h('button', btn('outline'), 'Reset check to the example');
    resetDraft.addEventListener('click', async () => {
      const ok = await confirmAction({
        title: 'Replace your check with the example?',
        description: 'The title, description and other fields on Check are replaced by the example. This can’t be undone.',
        confirm: 'Replace with example',
      });
      if (!ok) return;
      saveDraft(SAMPLE_DRAFT);
      location.reload();
    });
    const clearAll = h('button', btn('destructive'), 'Clear saved data');
    clearAll.addEventListener('click', async () => {
      const ok = await confirmAction({
        title: 'Clear saved data?',
        description: 'Your settings go back to their defaults, and your last check is deleted from this browser. This can’t be undone.',
        confirm: 'Clear data',
      });
      if (!ok) return;
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      resetSettings();
      location.reload();
    });
    const resets = card('s-reset', 'Reset',
      'Settings and your last check are saved in this browser only. Reset settings puts every setting back to its default. Reset check swaps your draft on Check for the example. Clear saved data does both.',
      h('div', { class: 'actions-row' }, resetAll, resetDraft, clearAll));

    // Put focus back where it was after the rebuild: by id, else by aria-label (the switches).
    const active = document.activeElement;
    const focusSel = active?.id ? `#${CSS.escape(active.id)}` : active?.getAttribute('aria-label') ? `[aria-label="${CSS.escape(active.getAttribute('aria-label')!)}"]` : null;
    clear(root, intro, profiles, th, defs, legend, resets);
    if (focusSel) root.querySelector<HTMLElement>(focusSel)?.focus();
  }

  onSettings(() => { if (!root.contains(document.activeElement) || !(document.activeElement instanceof HTMLInputElement && document.activeElement.type !== 'checkbox')) render(); });
  render();
  return root;
}
